/** Client-side review state for the aux-sync review page.
 *
 *  The record set is server-paginated, so the page never holds every key.
 *  Selection is therefore modeled as OVERRIDES on top of the server's
 *  per-record defaults (`selected_default`), and the approval payload uses
 *  the task's `default_overrides` mode — tiny payload, exact semantics,
 *  no need to enumerate unseen rows. Edits, manual additions and
 *  per-record options accumulate here until approval.
 */
import { useCallback, useMemo, useReducer } from 'react';

import type { AuxRecordSummary } from '@/api/auxSync';

export interface AddedRecordInput {
  kind: string;
  restech?: string | null;
  account?: string | null;
  data: Record<string, unknown>;
}

export interface ReviewState {
  /** key -> forced on/off (absent = server default). */
  overrides: Record<string, boolean>;
  /** key -> partial data overrides. */
  edits: Record<string, Record<string, unknown>>;
  added: AddedRecordInput[];
  /** key -> option flags (e.g. { link_user: true }). */
  options: Record<string, Record<string, boolean>>;
}

const INITIAL: ReviewState = { overrides: {}, edits: {}, added: [], options: {} };

type Action =
  | { type: 'toggle'; key: string; selected: boolean; serverDefault: boolean }
  | { type: 'bulk'; keys: string[]; selected: boolean; serverDefaults: Record<string, boolean> }
  | { type: 'edit'; key: string; data: Record<string, unknown> }
  | { type: 'clearEdit'; key: string }
  | { type: 'add'; record: AddedRecordInput }
  | { type: 'removeAdded'; index: number }
  | { type: 'option'; key: string; name: string; value: boolean }
  | { type: 'reset' };

function setOverride(
  overrides: Record<string, boolean>,
  key: string,
  selected: boolean,
  serverDefault: boolean,
): Record<string, boolean> {
  const next = { ...overrides };
  if (selected === serverDefault) {
    delete next[key]; // back to the default — no override to carry
  } else {
    next[key] = selected;
  }
  return next;
}

function reducer(state: ReviewState, action: Action): ReviewState {
  switch (action.type) {
    case 'toggle':
      return {
        ...state,
        overrides: setOverride(state.overrides, action.key, action.selected, action.serverDefault),
      };
    case 'bulk': {
      let overrides = state.overrides;
      for (const key of action.keys) {
        overrides = setOverride(
          overrides,
          key,
          action.selected,
          action.serverDefaults[key] ?? false,
        );
      }
      return { ...state, overrides };
    }
    case 'edit': {
      const merged = { ...(state.edits[action.key] ?? {}), ...action.data };
      return { ...state, edits: { ...state.edits, [action.key]: merged } };
    }
    case 'clearEdit': {
      const edits = { ...state.edits };
      delete edits[action.key];
      return { ...state, edits };
    }
    case 'add':
      return { ...state, added: [...state.added, action.record] };
    case 'removeAdded':
      return { ...state, added: state.added.filter((_, i) => i !== action.index) };
    case 'option': {
      const forKey = { ...(state.options[action.key] ?? {}) };
      if (action.value) {
        forKey[action.name] = true;
      } else {
        delete forKey[action.name];
      }
      const options = { ...state.options };
      if (Object.keys(forKey).length) {
        options[action.key] = forKey;
      } else {
        delete options[action.key];
      }
      return { ...state, options };
    }
    case 'reset':
      return INITIAL;
    default:
      return state;
  }
}

/** Effective selection of one row = server default unless overridden. */
export function isSelected(state: ReviewState, row: AuxRecordSummary): boolean {
  return state.overrides[row.key] ?? row.selected_default;
}

/** Effective data of one row = server data + local edits. */
export function effectiveData(
  state: ReviewState,
  row: AuxRecordSummary,
): Record<string, unknown> {
  const edit = state.edits[row.key];
  return edit ? { ...row.data, ...edit } : row.data;
}

/** Fields still blocking selection after local edits are applied. */
export function stillMissing(state: ReviewState, row: AuxRecordSummary): string[] {
  const data = effectiveData(state, row);
  return row.missing_for_selection.filter((f) => {
    const v = data[f];
    return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
  });
}

/** The approval payload this state expresses. `{}` when nothing changed —
 *  the server then applies its own default selection. */
export function buildApprovalPayload(state: ReviewState): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  const select = Object.keys(state.overrides).filter((k) => state.overrides[k]);
  const deselect = Object.keys(state.overrides).filter((k) => !state.overrides[k]);
  if (select.length || deselect.length) {
    payload.default_overrides = { select, deselect };
  }
  if (Object.keys(state.edits).length) {
    payload.edits = Object.fromEntries(
      Object.entries(state.edits).map(([key, data]) => [key, { data }]),
    );
  }
  if (state.added.length) payload.added_records = state.added;
  if (Object.keys(state.options).length) payload.options = state.options;
  return payload;
}

export function hasLocalChanges(state: ReviewState): boolean {
  return (
    Object.keys(state.overrides).length > 0 ||
    Object.keys(state.edits).length > 0 ||
    state.added.length > 0 ||
    Object.keys(state.options).length > 0
  );
}

export function useReviewState() {
  const [state, dispatch] = useReducer(reducer, INITIAL);
  const toggle = useCallback(
    (row: AuxRecordSummary, selected: boolean) =>
      dispatch({ type: 'toggle', key: row.key, selected, serverDefault: row.selected_default }),
    [],
  );
  const bulk = useCallback(
    (rows: AuxRecordSummary[], selected: boolean) =>
      dispatch({
        type: 'bulk',
        keys: rows.map((r) => r.key),
        selected,
        serverDefaults: Object.fromEntries(rows.map((r) => [r.key, r.selected_default])),
      }),
    [],
  );
  const edit = useCallback(
    (key: string, data: Record<string, unknown>) => dispatch({ type: 'edit', key, data }),
    [],
  );
  const clearEdit = useCallback((key: string) => dispatch({ type: 'clearEdit', key }), []);
  const add = useCallback((record: AddedRecordInput) => dispatch({ type: 'add', record }), []);
  const removeAdded = useCallback((index: number) => dispatch({ type: 'removeAdded', index }), []);
  const setOption = useCallback(
    (key: string, name: string, value: boolean) => dispatch({ type: 'option', key, name, value }),
    [],
  );
  const reset = useCallback(() => dispatch({ type: 'reset' }), []);
  return useMemo(
    () => ({ state, toggle, bulk, edit, clearEdit, add, removeAdded, setOption, reset }),
    [state, toggle, bulk, edit, clearEdit, add, removeAdded, setOption, reset],
  );
}
